import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import Brand from '../../../../../types/brand/Brand';
import BrandWriteDTO from '../../../../../types/brand/BrandWriteDTO';
import { IsAdminDirective } from '../../../directives/is-admin.directive';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { BrandService } from '../../../services/brand.service';
import { UpdateBrandDialogComponent } from '../../shared/brands/update-brand-dialog/update-brand-dialog.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

@Component({
    selector: 'app-brand-detail',
    templateUrl: './brand-detail.component.html',
    styleUrls: [ './brand-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateBrandDialogComponent,
        RoleNamePipe,
        IsAdminDirective
    ]
})
export class BrandDetailComponent implements OnInit {
    public brand: Brand | null = null;
    public editableBrand: BrandWriteDTO = new BrandWriteDTO({});
    public displayUpdateDialog = false;

    constructor(
        private route: ActivatedRoute,
        private brandService: BrandService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadBrand();
    }

    /**
     * Opens the edit dialog.
     */
    public openEditDialog(): void {
        this.editableBrand = new BrandWriteDTO(this.brand ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the brand details.
     */
    public reloadBrandDetails(): void {
        this.loadBrand();
    }

    /**
     * Confirms the deletion of the brand.
     */
    public confirmDelete(): void {
        if (!this.brand) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the brand '${ this.brand.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (!this.brand) {
                    return;
                }
                this.brandService.deleteBrand(this.brand.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Brand deleted successfully',
                        life: 3000
                    });
                    // Navigate back to the brands page
                    window.history.back();
                }).catch((error: any) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Loads the brand.
     */
    private loadBrand(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id, 10);
            this.brandService.getBrand(idNumber).then((result: Brand) => {
                this.brand = result;
            }).catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            });
        }
    }
}
